// src/features/media/useVideoMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaApi } from './MediaApi';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { TAG_QUERY_KEYS } from '@/lib/constants/tag';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { createBatchMutationHook } from '@/shared/hooks/useBatchMutation';
import type { VideoUpdateRequest, MergeCreateRequest, MediaTagsBatchRequest } from '@/lib/types/Media';

/**
 * Update video metadata mutation.
 * On success: updates detail cache and invalidates list.
 */
export function useUpdateVideo(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VideoUpdateRequest) =>
      MediaApi.updateVideo(selectedCId!, mediaId, data),
    onSuccess: (updatedVideo) => {
      // Update detail cache with fresh data
      queryClient.setQueryData(
        MEDIA_QUERY_KEYS.video(selectedCId!, mediaId),
        updatedVideo
      );
      // Invalidate list to reflect title/status changes
      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.videos(selectedCId!),
      });
      // Refresh tag suggestions (new tags become available for other videos)
      queryClient.invalidateQueries({
        queryKey: TAG_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}

/** Activate videos (batch). */
export const useActivateVideos = createBatchMutationHook(
  MediaApi.activateVideos,
  (cId) => MEDIA_QUERY_KEYS.videos(cId),
);

/** Deactivate videos (batch). */
export const useDeactivateVideos = createBatchMutationHook(
  MediaApi.deactivateVideos,
  (cId) => MEDIA_QUERY_KEYS.videos(cId),
);

/** Delete videos (batch). */
export const useDeleteVideos = createBatchMutationHook(
  MediaApi.deleteVideos,
  (cId) => MEDIA_QUERY_KEYS.videos(cId),
);

/**
 * Batch tag update via the set-based server endpoint (media/tags/batch) —
 * one request regardless of selection size. The server scopes the write to
 * media the customer owns and is idempotent (add = ensure present,
 * remove = ensure absent), so a retry after an error is always safe.
 */
export function useBatchUpdateVideoTags() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: MediaTagsBatchRequest) =>
      MediaApi.batchUpdateTags(selectedCId!, req),
    onSettled: () => {
      // videos(cId) is a prefix of every video detail key, so list AND open
      // details refresh; tag list refreshes for new tag suggestions.
      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.videos(selectedCId!),
      });
      queryClient.invalidateQueries({
        queryKey: TAG_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}

/** Merge multiple videos into a new video. */
export function useMergeVideos() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: MergeCreateRequest) =>
      MediaApi.createMerge(selectedCId!, req),
    onSuccess: (newVideo) => {
      // Cache the new merged video detail
      queryClient.setQueryData(
        MEDIA_QUERY_KEYS.video(selectedCId!, newVideo.media.mediaId),
        newVideo
      );
      // Refresh video list to include new merged video
      queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.videos(selectedCId!),
      });
      // Refresh tags (merged video may union tags from multiple sources)
      queryClient.invalidateQueries({
        queryKey: TAG_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}
